import { combineReducers, configureStore } from "@reduxjs/toolkit";
import chat from "store/slices/chat";
import { emptyApi } from "./apis/emptyApi";
import addonsSlice from "./slices/addons";
import billingFilters from "./slices/billingFilters";
import bookkeepingTracker from "./slices/bookkeepingTracker";
import booksOnboarding from "./slices/booksOnboarding";
import calendar from "./slices/calender";
import cartSubscription from "./slices/cartSubscription";
import channelInfoSlider from "./slices/channelInfoSlider";
import inkleAISlider from "./slices/inkleAISlider";
import channelPendingActions from "./slices/channelPendingActions";
import channelPinnedItems from "./slices/channelPinnedItems";
import chartOfAccounts from "./slices/chartOfAccounts";
import chatSearch from "./slices/chatSearch";
import chatUnreadCount from "./slices/chatUnreadCount";
import ticket from "./slices/ticket";
import credits from "./slices/credit";
import dashboard from "./slices/dashboard";
import appLayout from "./slices/appLayout";
import entitySlice from "./slices/entity";
import financialClosing from "./slices/financialClosing";
import ledgerFilter from "./slices/generalLedger";
import invoiceModalTypes from "./slices/invoiceModalTypes";
import invoices from "./slices/invoices";
import messageForward from "./slices/messageForward";
import messageSearch from "./slices/messageSearch";
import chatExpanded from "./slices/chatExpanded";
import messageToScrollTo from "./slices/messageToScrollTo";
import notification from "./slices/notification";
import onboarding from "./slices/onboarding";
import onboardingWidget from "./slices/onboardingWidget";
import openItem from "./slices/openItem";
import perks from "./slices/perks";
import platformTour from "./slices/platformTour";
import reconciliation from "./slices/reconciliation";
import registerAgent from "./slices/registerAgent";
import requestDocumentsInChat from "./slices/requestDocumentsInChat";
import reviewAndBalancePayment from "./slices/reviewAndBalancePayment";
import slack from "./slices/slack";
import subscriptionModal from "./slices/subscriptionModal";
import taskSlice from "./slices/task";
import task7004 from "./slices/task7004";
import taskList from "./slices/taskList";
import taskReview from "./slices/TaskReview";
import transactionFilter from "./slices/transactionFilter";
import openItemFilter from "./slices/openItemFilter";
import transactions from "./slices/transactions";
import tpPaymentsSlice from "./slices/transferpricing";
import vendors from "./slices/vendors";
import sidebar from "./slices/sidebar";
import connections from "./slices/connections";
import serviceList from "./slices/serviceList";
import ruleEngine from "./slices/ruleEngine";
import transactionsInChat from "./slices/transactionsInChat";
import vendorEmailRequestsInChat from "./slices/vendorEmailRequestsInChat";
import transactionCardPreview from "./slices/transactionCardPreview";
import fixedAsset from "./slices/fixedAsset";
import similarTransactions from "./slices/similarTransactions";
import intragroupList from "./slices/IntragroupService";
import salexTaxList from "./slices/SalesTaxService";
import splitTransaction from "./slices/splitTransaction";
import billingInvoiceSlider from "./slices/billingInvoiceSlider";
import practiceBilling from "./slices/practiceBilling";
import linkInkleInvoice from "./slices/linkInkleInvoice";
import openComment from "./slices/openComment";
import bankTransfer from "./slices/bankTransfer";
import cartInChat from "./slices/cartInChat";
import onboardingPlans from "./slices/onboardingPlans";
import subscriptionSummary from "./slices/subscriptionSummary";
import messageToQuote from "./slices/messegeIdToRemind";
import generateChatMessage from "./slices/generateChatMessage";
import svbConnect from "./slices/svbConnect";
import filingDraftReview from "./slices/filingDraftReview";
import TaxAIResponse from "./slices/TaxAIResponse";
import prepaidExpenseModal from "./slices/prepaidExpenseModal";
import fixedAssetModal from "./slices/fixedAssetModal";
import dftFilingDraftReview from "./slices/dftFilingDraftReview";
import estimatedTaxCardInChat from "./slices/estimatedTaxCardInChat";
import chaserDashboard from "./slices/chaserDashboard";
import documentTagReviewModal from "./slices/documentTagReviewModal";
import churnDetailsModal from "./slices/churnDetailsModal";

const rootReducer = combineReducers({
  [emptyApi.reducerPath]: emptyApi.reducer,
  subscriptionModal,
  chat,
  calendar,
  transactions,
  vendors,
  invoices,
  task7004,
  credits,
  onboarding,
  invoiceModalTypes,
  taskList,
  entitySlice,
  transactionFilter,
  openItemFilter,
  tpPaymentsSlice,
  dashboard,
  appLayout,
  notification,
  registerAgent,
  ticket,
  chatSearch,
  messageToScrollTo,
  chatUnreadCount,
  perks,
  addonsSlice,
  messageSearch,
  chatExpanded,
  billingFilters,
  financialClosing,
  onboardingWidget,
  platformTour,
  reviewAndBalancePayment,
  openItem,
  slack,
  taskReview,
  channelInfoSlider,
  inkleAISlider,
  channelPendingActions,
  channelPinnedItems,
  chartOfAccounts,
  requestDocumentsInChat,
  bankTransfer,
  reconciliation,
  cartSubscription,
  taskSlice,
  ledgerFilter,
  messageForward,
  bookkeepingTracker,
  sidebar,
  connections,
  booksOnboarding,
  serviceList,
  ruleEngine,
  transactionsInChat,
  transactionCardPreview,
  fixedAsset,
  similarTransactions,
  intragroupList,
  salexTaxList,
  splitTransaction,
  billingInvoiceSlider,
  practiceBilling,
  linkInkleInvoice,
  openComment,
  cartInChat,
  onboardingPlans,
  subscriptionSummary,
  messageToQuote,
  generateChatMessage,
  svbConnect,
  filingDraftReview,
  TaxAIResponse,
  prepaidExpenseModal,
  fixedAssetModal,
  vendorEmailRequestsInChat,
  dftFilingDraftReview,
  estimatedTaxCardInChat,
  chaserDashboard,
  documentTagReviewModal,
  churnDetailsModal,
});

export const setupStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(emptyApi.middleware),
  });
};

export const store = setupStore();

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = typeof store.dispatch;
